import { useState, type FormEvent } from 'react';

import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import { useIpdDaycareLookup, useUpdateIpdDaycareAdmission } from './ipd-daycare.queries';
import {
  AdmissionInfoFields,
  FieldLabel,
  PatientDemographicFields,
  PatientIdentityFields,
  ResponsiblePersonFields,
} from './ipd-daycare.shared';
import { toDateTimeInput } from './ipd-daycare.utils';
import type { IpdDaycareAdmission, PatientFormFields, ResponsiblePersonFormFields, UpdateIpdDaycareAdmissionPayload } from './types';

interface AdmissionInfoState {
  symptoms: string | null;
  diagnosisAtAdmission: string | null;
  underDoctor: string | null;
  otherUnderDoctor: string | null;
  bedCategory: string | null;
  bed: string | null;
  alcoholTobaccoHistory: string | null;
  pastMedicalSurgicalHistory: string | null;
  familyHistory: string | null;
  referredBy: string | null;
  provider: string | null;
  marketBy: string | null;
}

const patientFieldsFromAdmission = (row: IpdDaycareAdmission): PatientFormFields => ({
  patientId: row.patient.id,
  uhid: row.patientSnapshot.uhid,
  name: row.patientSnapshot.name,
  phone: row.patientSnapshot.phone ? normalizePhoneInput(row.patientSnapshot.phone) : null,
  gender: row.patientSnapshot.gender,
  maritalStatus: row.patientSnapshot.maritalStatus,
  guardianName: row.patientSnapshot.guardianName,
  relation: row.patientSnapshot.relation,
  alternativeNumber: row.patientSnapshot.alternativeNumber ? normalizePhoneInput(row.patientSnapshot.alternativeNumber) : null,
  dateOfBirth: row.patientSnapshot.dateOfBirth,
  ageYears: row.patientSnapshot.ageYears,
  ageMonths: row.patientSnapshot.ageMonths,
  ageDays: row.patientSnapshot.ageDays,
  address: row.patientSnapshot.address,
  state: row.patientSnapshot.state,
  district: row.patientSnapshot.district,
  pinCode: row.patientSnapshot.pinCode,
  aadhaar: row.patientSnapshot.aadhaar,
});

const admissionInfoFromAdmission = (row: IpdDaycareAdmission): AdmissionInfoState => ({
  symptoms: row.symptoms,
  diagnosisAtAdmission: row.diagnosisAtAdmission,
  underDoctor: row.underDoctor?.id ?? null,
  otherUnderDoctor: row.otherUnderDoctor?.id ?? null,
  bedCategory: row.bedCategory.id,
  bed: row.bed.id,
  alcoholTobaccoHistory: row.alcoholTobaccoHistory,
  pastMedicalSurgicalHistory: row.pastMedicalSurgicalHistory,
  familyHistory: row.familyHistory,
  referredBy: row.referredBy.id,
  provider: row.provider?.id ?? null,
  marketBy: row.marketBy?.id ?? null,
});

export const IpdEditAdmissionForm = ({ admission, onSaved }: { admission: IpdDaycareAdmission; onSaved: () => void }) => {
  const mutation = useUpdateIpdDaycareAdmission();
  const { showToast } = useToast();

  const [admissionDate, setAdmissionDate] = useState(() => toDateTimeInput(admission.admissionDate));
  const [packageType, setPackageType] = useState(admission.packageType);
  const [insuranceType, setInsuranceType] = useState(admission.insuranceType);
  const [insuranceNumber, setInsuranceNumber] = useState(admission.insuranceNumber ?? '');
  const [patient, setPatient] = useState<PatientFormFields>(() => patientFieldsFromAdmission(admission));
  const [admissionInfo, setAdmissionInfo] = useState<AdmissionInfoState>(() => admissionInfoFromAdmission(admission));
  const [responsiblePerson, setResponsiblePerson] = useState<ResponsiblePersonFormFields>(() => ({
    name: admission.responsiblePerson.name ?? '',
    relation: admission.responsiblePerson.relation ?? '',
    phone: admission.responsiblePerson.phone ? normalizePhoneInput(admission.responsiblePerson.phone) : '',
    age: admission.responsiblePerson.age,
    address: admission.responsiblePerson.address ?? '',
  }));

  const states = useIpdDaycareLookup('states');
  const districts = useIpdDaycareLookup('districts', '', { state: patient.state ?? '' });
  const doctors = useIpdDaycareLookup('doctors');
  const tpas = useIpdDaycareLookup('tpas');
  const referrals = useIpdDaycareLookup('referrals');
  const providers = useIpdDaycareLookup('providers');
  const marketByOptions = useIpdDaycareLookup('market-by');
  const tpaOptions = tpas.data ?? [];
  const insuranceTypeOptions = tpaOptions.some((item) => item.label === insuranceType) || !insuranceType
    ? tpaOptions
    : [{ id: `current-${insuranceType}`, label: insuranceType }, ...tpaOptions];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const payload: UpdateIpdDaycareAdmissionPayload = {
      name: patient.name,
      phone: patient.phone,
      gender: patient.gender,
      maritalStatus: patient.maritalStatus,
      guardianName: patient.guardianName,
      relation: patient.relation,
      alternativeNumber: patient.alternativeNumber,
      dateOfBirth: patient.dateOfBirth,
      ageYears: patient.ageYears,
      ageMonths: patient.ageMonths,
      ageDays: patient.ageDays,
      address: patient.address,
      state: patient.state,
      district: patient.district,
      pinCode: patient.pinCode,
      aadhaar: patient.aadhaar,
      admissionDate,
      packageType,
      insuranceType,
      insuranceNumber: insuranceNumber || null,
      symptoms: admissionInfo.symptoms,
      diagnosisAtAdmission: admissionInfo.diagnosisAtAdmission,
      otherUnderDoctor: admissionInfo.otherUnderDoctor,
      alcoholTobaccoHistory: admissionInfo.alcoholTobaccoHistory,
      pastMedicalSurgicalHistory: admissionInfo.pastMedicalSurgicalHistory,
      familyHistory: admissionInfo.familyHistory,
      referredBy: admissionInfo.referredBy,
      provider: admissionInfo.provider,
      marketBy: admissionInfo.marketBy,
      responsiblePerson,
    };
    void confirmUpdate('this IPD/Daycare admission').then((confirmed) => {
      if (!confirmed) return;
      mutation.mutate({ id: admission.id, payload }, {
        onSuccess: () => {
          showToast('Admission updated successfully.', 'success');
          setTimeout(onSaved, 2000);
        },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'IPD/Daycare admission could not be saved.', 'error'),
      });
    });
  };

  const busy = mutation.isPending;

  return (
    <form className="ipd-daycare-registration-card" onSubmit={submit}>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Edit Admission — {admission.patientSnapshot.name}</span></div>
        <p>IPD No {admission.ipdNumber} · {admission.admissionType} (not editable) · Under Doctor {doctorNameOrDash(admission.underDoctor?.name)} (not editable) · Bed {admission.bed.name} (change via Bed Transfer)</p>
      </header>

      <section className="ipd-daycare-section">
        <div className="ipd-daycare-top-row">
          <label><FieldLabel required>Admission Date &amp; time</FieldLabel><input type="datetime-local" value={admissionDate} onChange={(event) => setAdmissionDate(event.target.value)} required /></label>
          <label><FieldLabel required>Package Type</FieldLabel><select value={packageType} onChange={(event) => setPackageType(event.target.value)} required><option>General Package</option><option>Package with Medicine</option><option>Conservative Medicine</option><option>Surgical Package</option></select></label>
          <label><FieldLabel>Insurance Type</FieldLabel><select aria-label="Insurance Type" value={insuranceType} onChange={(event) => setInsuranceType(event.target.value)}>{insuranceTypeOptions.map((option) => <option key={option.id} value={option.label}>{option.label}</option>)}</select></label>
          <label><FieldLabel>Insurance No.</FieldLabel><input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} maxLength={100} /></label>
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Patient Details</h2>
        <div className="ipd-daycare-patient-row1">
          <PatientIdentityFields value={patient} onChange={setPatient} />
        </div>
        <div className="ipd-daycare-patient-row2">
          <PatientDemographicFields value={patient} onChange={setPatient} states={states.data} districts={districts.data} />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Patient Admission Information</h2>
        <div className="ipd-daycare-admission-row">
          <AdmissionInfoFields
            value={admissionInfo}
            onChange={setAdmissionInfo}
            doctors={doctors.data}
            referrals={referrals.data}
            providers={providers.data}
            marketByOptions={marketByOptions.data}
            lockedDoctorAndBed={{ underDoctorName: doctorNameOrDash(admission.underDoctor?.name), bedCategoryName: admission.bedCategory.name, bedName: admission.bed.name }}
          />
        </div>
      </section>

      <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
        <div className="ipd-daycare-responsible-row">
          <ResponsiblePersonFields value={responsiblePerson} onChange={setResponsiblePerson} />
        </div>
      </section>

      <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save Changes'}</button></div>
    </form>
  );
};
